import { CustomerBookingNotificationPayload, Notification } from "shared/types";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { localTzDate } from "shared/utils/dayjs";
import { translateMessage } from "shared/utils";
import { textPx } from "shared/constants/zoom";

// Body of a customer-booking notification (booked / waitlisted / cancelled /
// no_show / left_waitlist; also a private session an instructor created or
// cancelled from the instructor app, which rides the same booked / cancelled
// types with the instructor named in the outcome), laid out to be read in one pass and shared by the
// dashboard rail and the bell drawer so the two can never drift: the class in
// ink (the thing that matters) with when it runs in muted grey, then the seat
// outcome as one quiet grey label, only when there is one. Reads the
// structured payload; legacy rows (pre-payload) are parsed from the stored
// `content`, whose shape the server fixed as "class · DD MMM HH:mm<br/>outcome".

const stripTags = (html: string) => html.replace(/<[^>]+>/g, "").trim();

const LEGACY_TYPES = ["booked", "waitlisted", "cancelled", "no_show", "left_waitlist"];

export const customerBookingBody = (
  notification: Notification,
): { className: string; when: string; outcome: string | null } | null => {
  const payload = notification.payload;
  if (payload && "kind" in payload && payload.kind === "customer_booking") {
    const p = payload as CustomerBookingNotificationPayload;
    const when = localTzDate(p.occurrence_timestamp).format("ddd, D MMM · HH:mm");
    const outcome =
      p.outcome.type === "promoted"
        ? translateWithVars("notifications.client_booking.outcome.promoted", {
            name: p.outcome.name,
          })
        : p.outcome.type === "queued"
          ? translateWithVars("notifications.client_booking.outcome.queued", {
              count: p.outcome.waitlist_count,
            })
          : p.outcome.type === "slot_open"
            ? translate("notifications.client_booking.outcome.slot_open")
            : p.outcome.type === "instructor_booked" ||
                p.outcome.type === "instructor_cancelled"
              ? translateWithVars(
                  `notifications.client_booking.outcome.${p.outcome.type}`,
                  { name: p.outcome.name },
                )
              : null;
    return { className: p.class_name, when, outcome };
  }
  if (!notification.type || !LEGACY_TYPES.includes(notification.type)) return null;
  const [first = "", ...rest] = (notification.content ?? "").split("<br/>");
  const sep = first.lastIndexOf(" · ");
  if (sep < 0) return null;
  // Legacy pieces, presented in the same shape as payload rows: the stored
  // "15 Sep 17:54" gets the date · time separator (no weekday: the stored
  // string carries no year, so re-deriving one would be a guess), and the
  // sentence's comma becomes the label's separator.
  const outcomeText = stripTags(translateMessage(rest.join(" ")))
    .replace(/[.,]\s*$/, "")
    .replace(/,\s+/g, " · ");
  const when = first.slice(sep + 3).replace(/^(\d{1,2} [A-Za-z]+) (\d{2}:\d{2})$/, "$1 · $2");
  return {
    className: first.slice(0, sep),
    when,
    outcome: outcomeText || null,
  };
};

export const CustomerBookingContent = ({
  body,
}: {
  body: NonNullable<ReturnType<typeof customerBookingBody>>;
}) => (
  <>
    <div style={{ marginTop: 3, fontSize: textPx(12.5), lineHeight: 1.4 }}>
      <span style={{ color: colors.ink, fontWeight: 500 }}>{body.className}</span>
      <span style={{ color: colors.ink3 }}> · {body.when}</span>
    </div>
    {body.outcome && (
      <div style={{ marginTop: 7 }}>
        <span
          style={{
            display: "inline-block",
            padding: "3px 9px",
            borderRadius: 6,
            fontSize: textPx(11.5),
            fontWeight: 500,
            lineHeight: `${textPx(16)}px`,
            background: colors.bgSoft,
            color: colors.ink2,
          }}
        >
          {body.outcome}
        </span>
      </div>
    )}
  </>
);
